import React from 'react';
import { TaraCard, TaraCardHeader, TaraCardTitle, TaraCardContent } from '../ui/TaraCard';
import TaraBadge from '../ui/TaraBadge';
import { BookOpen, Brain, Target, Clock, Award } from 'lucide-react';

const EducationPath = () => {
  return (
    <div className="space-y-6">
      {/* Learning Intelligence Score */}
      <TaraCard variant="cosmic">
        <TaraCardHeader>
          <TaraCardTitle>Learning Intelligence Profile</TaraCardTitle>
          <p className="text-sm text-stone-600 mt-1">
            Your cognitive strengths and optimal learning windows
          </p>
        </TaraCardHeader>
        <TaraCardContent className="p-6">
          <div className="grid grid-cols-2 gap-4 mb-6">
            <div className="text-center p-4 bg-indigo-50 rounded-lg">
              <Brain className="w-6 h-6 text-indigo-600 mx-auto mb-2" />
              <div className="text-2xl font-bold text-indigo-600 mb-1">82%</div>
              <p className="text-xs text-stone-600">Logical Intelligence</p>
            </div>
            
            <div className="text-center p-4 bg-purple-50 rounded-lg">
              <Award className="w-6 h-6 text-purple-600 mx-auto mb-2" />
              <div className="text-2xl font-bold text-purple-600 mb-1">75%</div>
              <p className="text-xs text-stone-600">Creative Intelligence</p>
            </div>
          </div>

          <div className="space-y-2 text-sm">
            <div className="flex items-center justify-between p-3 bg-stone-50 rounded-lg">
              <span className="text-stone-900 font-medium">Mercury Strength</span>
              <TaraBadge variant="vibrant" size="sm">Excellent</TaraBadge>
            </div>
            <div className="flex items-center justify-between p-3 bg-stone-50 rounded-lg">
              <span className="text-stone-900 font-medium">5th House Activation</span>
              <TaraBadge variant="calm" size="sm">Strong</TaraBadge>
            </div>
          </div>
        </TaraCardContent>
      </TaraCard>

      {/* Optimal Study Windows */}
      <TaraCard>
        <TaraCardHeader>
          <TaraCardTitle className="flex items-center gap-2">
            <Clock className="w-5 h-5 text-indigo-600" />
            Best Study Times Today
          </TaraCardTitle>
        </TaraCardHeader>
        <TaraCardContent className="p-6">
          <div className="space-y-3">
            <div className="p-4 bg-gradient-to-br from-green-50 to-emerald-50 rounded-lg border-l-4 border-green-500">
              <div className="flex items-center justify-between mb-1">
                <p className="text-sm font-bold text-stone-900">6:00 AM - 9:00 AM</p>
                <TaraBadge variant="vibrant" size="sm">Peak</TaraBadge>
              </div>
              <p className="text-xs text-stone-600">
                Maximum Mercury energy. Best for complex subjects, math, and problem-solving.
              </p>
            </div>

            <div className="p-4 bg-gradient-to-br from-blue-50 to-indigo-50 rounded-lg border-l-4 border-blue-500">
              <div className="flex items-center justify-between mb-1">
                <p className="text-sm font-bold text-stone-900">4:00 PM - 6:00 PM</p>
                <TaraBadge variant="calm" size="sm">Good</TaraBadge>
              </div>
              <p className="text-xs text-stone-600">
                Creative energy rises. Ideal for arts, writing, and conceptual learning.
              </p>
            </div>

            <div className="p-4 bg-stone-50 rounded-lg border-l-4 border-stone-300">
              <div className="flex items-center justify-between mb-1">
                <p className="text-sm font-bold text-stone-900">8:00 PM - 10:00 PM</p>
                <TaraBadge variant="neutral" size="sm">Avoid</TaraBadge>
              </div>
              <p className="text-xs text-stone-600">
                Moon energy low. Memory retention drops by 40%. Prefer light revision only.
              </p>
            </div>
          </div>
        </TaraCardContent>
      </TaraCard>

      {/* Recommended Skills */}
      <TaraCard>
        <TaraCardHeader>
          <TaraCardTitle className="flex items-center gap-2">
            <Target className="w-5 h-5 text-purple-600" />
            Skills to Focus On
          </TaraCardTitle>
          <p className="text-sm text-stone-600 mt-1">
            Based on your planetary strengths
          </p>
        </TaraCardHeader>
        <TaraCardContent className="p-6">
          <div className="space-y-4">
            <div>
              <div className="flex items-center justify-between mb-2">
                <p className="text-sm font-medium text-stone-900">Technical/Analytical Skills</p>
                <TaraBadge variant="vibrant" size="sm">Highest Potential</TaraBadge>
              </div>
              <p className="text-xs text-stone-600 mb-2">
                Mercury + Saturn favor technical mastery. Programming, data analysis, engineering highly favorable.
              </p>
              <div className="flex flex-wrap gap-2">
                <span className="px-2 py-1 bg-indigo-100 text-indigo-700 text-xs rounded-full">Coding</span>
                <span className="px-2 py-1 bg-indigo-100 text-indigo-700 text-xs rounded-full">Data Science</span>
                <span className="px-2 py-1 bg-indigo-100 text-indigo-700 text-xs rounded-full">AI/ML</span>
              </div>
            </div>

            <div>
              <div className="flex items-center justify-between mb-2">
                <p className="text-sm font-medium text-stone-900">Creative/Artistic Skills</p>
                <TaraBadge variant="calm" size="sm">Strong Phase (2025-26)</TaraBadge>
              </div>
              <p className="text-xs text-stone-600 mb-2">
                Venus dasha activates creative intelligence. Favorable for design, writing, music.
              </p>
              <div className="flex flex-wrap gap-2">
                <span className="px-2 py-1 bg-purple-100 text-purple-700 text-xs rounded-full">Design</span>
                <span className="px-2 py-1 bg-purple-100 text-purple-700 text-xs rounded-full">Writing</span>
                <span className="px-2 py-1 bg-purple-100 text-purple-700 text-xs rounded-full">Music</span>
              </div>
            </div>
          </div>
        </TaraCardContent>
      </TaraCard>

      {/* Study Strategy */}
      <TaraCard variant="sacred">
        <TaraCardHeader>
          <TaraCardTitle className="flex items-center gap-2">
            <BookOpen className="w-5 h-5 text-indigo-600" />
            Vidya's Study Strategy
          </TaraCardTitle>
        </TaraCardHeader>
        <TaraCardContent className="p-6">
          <div className="space-y-3 text-sm">
            <div className="flex items-start gap-3">
              <div className="text-lg">📅</div>
              <div>
                <p className="font-medium text-stone-900 mb-0.5">Exam Timing</p>
                <p className="text-xs text-stone-600">
                  Schedule important exams on Wednesdays or Thursdays when Mercury & Jupiter are strong.
                </p>
              </div>
            </div>

            <div className="flex items-start gap-3">
              <div className="text-lg">🧠</div>
              <div>
                <p className="font-medium text-stone-900 mb-0.5">Memory Boost</p>
                <p className="text-xs text-stone-600">
                  Moon weak this week. Use visual aids, diagrams, and mind maps for better retention.
                </p>
              </div>
            </div>

            <div className="flex items-start gap-3">
              <div className="text-lg">⏰</div>
              <div>
                <p className="font-medium text-stone-900 mb-0.5">Learning Method</p>
                <p className="text-xs text-stone-600">
                  Your chart favors active learning. Practice problems over passive reading.
                </p>
              </div>
            </div>
          </div>
        </TaraCardContent>
      </TaraCard>
    </div>
  );
};

export default EducationPath;
